import AutocompleteWrapper from '@/components/AutocompleteWrapper/AutocompleteWrapper'
import { DEFAULT_COLLISION_AVOIDANCE } from '@/components/SearchForm/constants'
import HorizonBundleAndSave from '@/components/SearchFormExperiment/utils/HorizonBundleAndSave'
import { fireInfantErrorEvent } from '@/components/TravelerSelection/ga4'
import type { GA4PageNameType } from '@/ga4types'
import useTabGroup from '@/hooks/useTabGroup'
import { getDateFieldErrorMessage } from '@/shared-utils/error-helpers'
import logSearchFormAnalytics from '@/shared-utils/log-search-form-analytics'
import type { CABIN_CLASS_TYPE, FLIGHT_TYPE, LOCATION_SEARCH_TYPE, TripType } from '@/types'
import { yupResolver } from '@hookform/resolvers/yup'
import {
  Button,
  cn,
  DatePicker,
  RadioGroup,
  Select,
  TRANSFORMER_MAX_MOBILE_WIDTH_PX,
  TravelerSelection,
  type DateRange,
  type ITravelerInfo,
} from '@pcln/horizon'
import { HorizonFlyDatePicker } from '@pcln/horizon-fly-date-picker'
import { addDays } from 'date-fns/addDays'
import { addMonths } from 'date-fns/addMonths'
import { type ParsedUrlQuery } from 'node:querystring'
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { Controller, useForm } from 'react-hook-form'
import searchFormSchema from '../SearchForm/validation'
import { MULTI_DESTINATION, ONE_WAY, ROUND_TRIP } from '../constants'
import { fireBundleAndSaveEvent } from '../ga4'
import { getFlightTypeClass, getGridContainerClass, getInputClass } from './HorizonFlightForm.variants'
import { CABIN_OPTIONS, DEFAULT_VALUES, LABELS, PLACEHOLDERS } from './constants'
import useFlightFormSubmitValues from './useFlightFormSubmitValues'
import useInitialFlightFormValues from './useInitialFlightFormValues'

import { useMediaQuery } from 'react-responsive'
import { isRoundTrip } from '../SearchForm/utils'
import type { FlightFormStateType } from '../types'
import {
  addFlightOnClick,
  createRadioOptions,
  getMultiDestDefaultMonth,
  getMultiDestDisabledDates,
  getMultiDestinationSelectedDate,
  getSelectedDates,
  onCalendarChange,
} from './utils/formHandlers'

type FlightSearchFormProps = {
  isRebook?: boolean
  isModify?: boolean
  onSubmit: (pageName: GA4PageNameType, data: FlightFormStateType, queryParams?: ParsedUrlQuery) => void
  pageName?: GA4PageNameType
  useCompactLayout?: boolean
}

export default function FlightSearchForm({
  isRebook = false,
  isModify = false,
  onSubmit,
  pageName = 'homepage',
  useCompactLayout = false,
}: FlightSearchFormProps) {
  const { initialValues, query, isMobile, cguid, appName, appVersion } = useInitialFlightFormValues(
    isRebook,
    isModify,
  )

  const isMobileComponent = useMediaQuery({ maxWidth: TRANSFORMER_MAX_MOBILE_WIDTH_PX })
  const {
    control,
    setValue,
    handleSubmit,
    clearErrors,
    formState: { errors },
  } = useForm<FlightFormStateType>({
    defaultValues: initialValues,
    mode: 'onSubmit',
    resolver: yupResolver(searchFormSchema),
    reValidateMode: 'onChange',
  })

  const { watchFlightType, watchFlights, watchCabinClass, watchTravelers, watchTripType } =
    useFlightFormSubmitValues({
      control,
      clearErrors,
      initialValues,
    })

  const startId = watchFlights[0]?.startLocation?.id ?? ''
  const startCityCode = watchFlights[0]?.startLocation?.cityCode ?? ''
  const endId = watchFlights[0]?.endLocation?.id ?? ''
  const endCityCode = watchFlights[0]?.endLocation?.cityCode ?? ''

  const flightDetails = useMemo(
    () => ({
      cabinClass: watchCabinClass || DEFAULT_VALUES.CABIN_CLASS,
      tripType: watchFlightType || DEFAULT_VALUES.FLIGHT_TYPE,
      flights: [
        {
          departureAirport: { id: startId, cityCode: startCityCode },
          arrivalAirport: { id: endId, cityCode: endCityCode },
        },
      ],
    }),
    [watchCabinClass, watchFlightType, startId, startCityCode, endId, endCityCode],
  )

  const [addHotel, setAddHotel] = useState<boolean>(DEFAULT_VALUES.ADD_HOTEL)
  const [addCar, setAddCar] = useState<boolean>(DEFAULT_VALUES.ADD_CAR)
  const { registerTabElement, focusNextElement } = useTabGroup()
  const fieldRefs = useRef<Map<string, HTMLInputElement>>(new Map())

  const flights = useMemo(
    () =>
      watchFlights ?? [
        {
          startLocation: {} as LOCATION_SEARCH_TYPE,
          endLocation: {} as LOCATION_SEARCH_TYPE,
          startDate: '',
          endDate: '',
        },
      ],
    [watchFlights],
  )

  // Stable unique IDs per flight slot so Fragment keys survive autocomplete
  // selection (no remount) yet still correctly unmount on flight removal.
  const flightKeyCounter = useRef(flights.length)
  const [flightKeys, setFlightKeys] = useState<number[]>(() =>
    Array.from({ length: flights.length }, (_, i) => i),
  )

  const isPackage = addHotel || addCar

  const onItemSelect = useCallback(
    (
      searchKey: `flights.${number}.startLocation` | `flights.${number}.endLocation`,
      item?: LOCATION_SEARCH_TYPE,
      fromElement?: HTMLInputElement,
    ) => {
      setValue(searchKey, item)
      if (!isMobile && !!item && typeof focusNextElement === 'function') {
        focusNextElement(true, fromElement)
      } else if (isMobile && !!item && document.activeElement instanceof HTMLElement) {
        document.activeElement.blur()
      }
    },
    [setValue, isMobile, focusNextElement],
  )

  const [bundleSelections, setBundleSelections] = useState<{
    [key: string]: boolean
  }>({
    STAY: addHotel,
    DRIVE: addCar,
  })

  const handleBundleSelectionChange = useCallback(
    (updatedTripType: string, checked: boolean, optionValue: string) => {
      setBundleSelections((prev) => ({
        ...prev,
        [optionValue]: checked,
      }))
      setValue('tripType', updatedTripType as TripType)
      if (checked) {
        try {
          fireBundleAndSaveEvent(updatedTripType)
        } catch {
          // GA4TripTypeMapper may throw on unexpected trip type combinations
        }
      }
      if (optionValue === 'STAY') {
        setAddHotel(checked)
        const newRooms = checked ? 1 : 0
        setValue('travelers', {
          ...watchTravelers,
          rooms: newRooms,
        })
      } else if (optionValue === 'DRIVE') {
        setAddCar(checked)
      }
    },
    [setValue, watchTravelers],
  )
  const currentDate = new Date()
  const calendarMaxMonth = addMonths(currentDate, 11)

  const [maxSelectableDate, setMaxSelectableDate] = useState<Date | undefined>()
  const [defaultMonth, setDefaultMonth] = useState<Date>(currentDate)

  const handleFlightCalendarChange = useCallback(
    (dates: Date | Date[] | DateRange | undefined) => {
      onCalendarChange(dates, setDefaultMonth, setValue, 0)
      if (addHotel && dates && 'from' in dates && dates.from && dates?.to === undefined) {
        setMaxSelectableDate(addDays(dates.from, 28))
      } else {
        setMaxSelectableDate(undefined)
      }
    },
    [addHotel, setValue],
  )

  const calendarStartMonth = useMemo(() => new Date(), [])
  const calendarEndMonth = useMemo(() => addMonths(new Date(), 11), [])

  const flightDatePickerSelected = useMemo(
    () => getSelectedDates(0, flights, watchFlightType as FLIGHT_TYPE, DEFAULT_VALUES.FLIGHT_TYPE),
    [flights, watchFlightType],
  )
  const flightDatePickerErrorMessage = useMemo(
    () => getDateFieldErrorMessage(errors, 0, isRoundTrip(watchFlightType as FLIGHT_TYPE)),
    [errors, watchFlightType],
  )
  const flightDatePickerApolloClientInfo = useMemo(
    () => ({ clientName: appName, clientVersion: appVersion }),
    [appName, appVersion],
  )
  const flightDatePickerDisabled = useMemo(
    () => ({ before: calendarStartMonth, after: maxSelectableDate }),
    [calendarStartMonth, maxSelectableDate],
  )
  const flightDatePickerTitle = useMemo(
    () =>
      (watchFlightType || DEFAULT_VALUES.FLIGHT_TYPE) === ROUND_TRIP
        ? 'Flights: Departing – Returning'
        : 'Flights: Departing',
    [watchFlightType],
  )
  const flightDatePickerLabel = useMemo(
    () =>
      (watchFlightType || DEFAULT_VALUES.FLIGHT_TYPE) === ROUND_TRIP ? 'Departing – Returning' : 'Departing',
    [watchFlightType],
  )
  const flightDatePickerMode =
    (watchFlightType || DEFAULT_VALUES.FLIGHT_TYPE) === ROUND_TRIP ? 'range' : 'single'
  const flightDatePickerNumberOfMonths = isMobileComponent ? 12 : 2
  const flightDatePickerMin = addHotel ? 1 : 0

  const lapInfantError = errors?.travelers?.lapInfants?.message
  useEffect(() => {
    if (lapInfantError) {
      fireInfantErrorEvent(pageName, 'air')
    }
  }, [lapInfantError, pageName])

  return (
    <div className='w-auto'>
      <form
        onSubmit={handleSubmit((values: FlightFormStateType) => {
          if (isMobile && typeof window.PCLN?.pclnEventDispatcher?.dispatch === 'function') {
            window.PCLN.pclnEventDispatcher.dispatch('FIND_YOUR_FLIGHT')
            const bookingInfo = {
              originCityName: values.flights[0].startLocation?.cityName,
              originAirportCode: values.flights[0].startLocation?.id,
              destinationCityName: values.flights[0].endLocation?.cityName,
              destinationCityAirportCode: values.flights[0].endLocation?.id,
              departureDate: values.flights[0].startDate,
              returnDate: values.flights[0].endDate,
            }
            window.PCLN.pclnEventDispatcher.dispatch('FIND_YOUR_FLIGHT_DATA', bookingInfo)
          }
          onSubmit?.(pageName, values, query)
          logSearchFormAnalytics(cguid, pageName, 'flights', isMobile)
        })}
        className='w-auto'
        aria-label='horizon-flight-search-form'
      >
        <div className='mx-auto w-auto'>
          <div className={getGridContainerClass(watchFlightType, addHotel, isPackage, useCompactLayout)}>
            {/* Flight Type Radio Buttons */}
            <div className={getFlightTypeClass(watchFlightType || DEFAULT_VALUES.FLIGHT_TYPE)}>
              <RadioGroup
                className='flex-row gap-1'
                name='flightType'
                options={createRadioOptions()}
                defaultValue='round-trip'
                required
                aria-labelledby='flight-type'
                handleChange={(value: unknown) => {
                  setValue('flightType', value as TripType)
                }}
              />
              {/* Cabin class dropdown - conditionally rendered (hidden in compact mode) */}
              {!isPackage && !useCompactLayout && (
                <div className='hidden lg:block'>
                  <Select
                    size='sm'
                    label={LABELS.CABIN_CLASS}
                    menuItems={[...CABIN_OPTIONS]}
                    initialValue={watchCabinClass || DEFAULT_VALUES.CABIN_CLASS}
                    onChange={(value: string) => {
                      setValue('cabinClass', value as CABIN_CLASS_TYPE, {
                        shouldDirty: true,
                      })
                    }}
                    placeholder={PLACEHOLDERS.CABIN_CLASS}
                    isLabelHidden
                  />
                </div>
              )}
            </div>

            {/* Multi-destination flights rendering */}
            {watchFlightType === MULTI_DESTINATION ? (
              <>
                {flights.map((flight, index) => (
                  <Fragment key={`flight-${flightKeys[index]}`}>
                    <div className='col-span-full flex items-center gap-4'>
                      <span className='text-base lg:text-sm font-bold'>Flight {index + 1}</span>
                      {index > 0 && (
                        <Button
                          type='tertiary'
                          size='sm'
                          buttonType='button'
                          className='**:text-xs min-h-auto **:font-medium'
                          onClick={() => {
                            const updatedFlights = [...flights.slice(0, index), ...flights.slice(index + 1)]
                            setValue('flights', updatedFlights)
                            setFlightKeys((prev) => [...prev.slice(0, index), ...prev.slice(index + 1)])
                          }}
                          aria-label={`Remove flight ${index + 1}`}
                        >
                          Remove
                        </Button>
                      )}
                    </div>

                    {/* Origin Input */}
                    <AutocompleteWrapper
                      searchKey='startLocation'
                      label='Departing from?'
                      typeAheadEndpoint='flights'
                      errorMessage={errors.flights?.[index]?.startLocation?.message}
                      initialSelectedItem={flight.startLocation}
                      onItemSelect={(itemSelected) => {
                        onItemSelect(
                          `flights.${index}.startLocation`,
                          itemSelected ?? undefined,
                          fieldRefs.current.get(`md-origin-${index}`),
                        )
                      }}
                      ref={(elem: HTMLInputElement) => {
                        registerTabElement(elem)
                        if (elem) fieldRefs.current.set(`md-origin-${index}`, elem)
                      }}
                    />

                    {/* Destination Input */}
                    <AutocompleteWrapper
                      searchKey='endLocation'
                      label='Going to?'
                      typeAheadEndpoint='flights'
                      errorMessage={errors.flights?.[index]?.endLocation?.message}
                      initialSelectedItem={flight.endLocation}
                      onItemSelect={(itemSelected) => {
                        onItemSelect(
                          `flights.${index}.endLocation`,
                          itemSelected ?? undefined,
                          fieldRefs.current.get(`md-dest-${index}`),
                        )
                      }}
                      ref={(elem: HTMLInputElement) => {
                        registerTabElement(elem)
                        if (elem) fieldRefs.current.set(`md-dest-${index}`, elem)
                      }}
                    />

                    {/* Departure Date */}
                    <div className={getInputClass('multiDestDateWrapper')}>
                      <DatePicker
                        className='flight-dates'
                        title='Flights: Departure'
                        label='Departing'
                        mode='single'
                        numberOfMonths={isMobileComponent ? 12 : 2}
                        selected={getMultiDestinationSelectedDate(index, flights)}
                        errorMessage={getDateFieldErrorMessage(errors, 0, isRoundTrip(watchFlightType))}
                        handleCalendarChange={(dates) => {
                          onCalendarChange(dates, setDefaultMonth, setValue, index)
                        }}
                        disabled={{
                          before: getMultiDestDisabledDates(flights[index - 1]?.startDate),
                        }}
                        startMonth={currentDate}
                        endMonth={calendarMaxMonth}
                        defaultMonth={getMultiDestDefaultMonth(
                          flights[index - 1]?.startDate,
                          flight.startDate,
                        )}
                        inputRef={registerTabElement}
                        collisionAvoidance={DEFAULT_COLLISION_AVOIDANCE}
                      />
                    </div>
                  </Fragment>
                ))}

                {/* Add another flight button */}
                <div className={cn('col-span-full', !useCompactLayout && 'lg:[grid-area:unset]')}>
                  <Button
                    type='secondary'
                    size='sm'
                    buttonType='button'
                    onClick={() => {
                      addFlightOnClick(setValue, watchFlights)
                      setFlightKeys((prev) => [...prev, flightKeyCounter.current++])
                    }}
                    disabled={watchFlights && watchFlights.length >= 5}
                  >
                    Add Another Flight
                  </Button>
                </div>
              </>
            ) : (
              <>
                {/* Single/Round-trip flight inputs */}
                <div className={getInputClass('origin')}>
                  <AutocompleteWrapper
                    searchKey='startLocation'
                    label='Departing from?'
                    typeAheadEndpoint='flights'
                    errorMessage={errors.flights?.[0]?.startLocation?.message}
                    initialSelectedItem={flights[0]?.startLocation}
                    onItemSelect={(itemSelected) => {
                      onItemSelect(
                        'flights.0.startLocation',
                        itemSelected ?? undefined,
                        fieldRefs.current.get('origin'),
                      )
                    }}
                    ref={(elem: HTMLInputElement) => {
                      registerTabElement(elem)
                      if (elem) fieldRefs.current.set('origin', elem)
                    }}
                  />
                </div>

                <div className={getInputClass('destination')}>
                  <AutocompleteWrapper
                    searchKey='endLocation'
                    label='Going to?'
                    typeAheadEndpoint='flights'
                    initialSelectedItem={flights[0]?.endLocation}
                    errorMessage={errors.flights?.[0]?.endLocation?.message}
                    onItemSelect={(itemSelected) => {
                      onItemSelect(
                        'flights.0.endLocation',
                        itemSelected ?? undefined,
                        fieldRefs.current.get('dest'),
                      )
                    }}
                    ref={(elem: HTMLInputElement) => {
                      registerTabElement(elem)
                      if (elem) fieldRefs.current.set('dest', elem)
                    }}
                  />
                </div>

                <div className={getInputClass('timeline', watchFlightType || DEFAULT_VALUES.FLIGHT_TYPE)}>
                  <HorizonFlyDatePicker
                    key={watchFlightType}
                    className='flight-dates'
                    title={flightDatePickerTitle}
                    label={flightDatePickerLabel}
                    mode={flightDatePickerMode}
                    numberOfMonths={flightDatePickerNumberOfMonths}
                    selected={flightDatePickerSelected}
                    errorMessage={flightDatePickerErrorMessage}
                    handleCalendarChange={handleFlightCalendarChange}
                    inputRef={registerTabElement}
                    flightDetails={flightDetails}
                    apolloClientInfo={flightDatePickerApolloClientInfo}
                    disabled={flightDatePickerDisabled}
                    startMonth={calendarStartMonth}
                    endMonth={calendarEndMonth}
                    defaultMonth={defaultMonth}
                    min={flightDatePickerMin}
                    collisionAvoidance={DEFAULT_COLLISION_AVOIDANCE}
                  />
                </div>
              </>
            )}

            {/* Room with Passengers /Passengers only Input (when hotel package is added) */}
            <div className={getInputClass('passengers', watchFlightType || DEFAULT_VALUES.FLIGHT_TYPE)}>
              <Controller
                control={control}
                name='travelers'
                defaultValue={watchTravelers}
                render={({ field }) => {
                  // eslint-disable-next-line @typescript-eslint/no-unused-vars
                  const { ref, ...rest } = field
                  return (
                    <TravelerSelection
                      {...rest}
                      isHotel={addHotel}
                      roomsEnabled={addHotel}
                      tooltipErrorMessageId='travelerSelectionError'
                      errorMessage={errors?.travelers?.lapInfants?.message || ''}
                      initialValues={field.value}
                      shouldCountLapInfant
                      maxChildren={6}
                      isTravelerSelectionDisabled={isRebook || isModify}
                      onChange={(values: ITravelerInfo) => {
                        field.onChange({
                          adults: values.adults,
                          children: values.children,
                          lapInfants: values.lapInfants,
                          childrenAges: values.childrenAges,
                          rooms: values.rooms,
                        })
                      }}
                      collisionAvoidance={DEFAULT_COLLISION_AVOIDANCE}
                    />
                  )
                }}
              />
            </div>

            {/* Cabin Class - conditionally rendered */}
            {!isPackage && (
              <Select
                label={LABELS.CABIN_CLASS}
                isInset
                iconLeft='airline_seat_recline_normal'
                menuItems={[...CABIN_OPTIONS]}
                initialValue={watchCabinClass}
                placeholder={PLACEHOLDERS.CABIN_CLASS}
                className={getInputClass('cabin', watchFlightType, useCompactLayout)}
                onChange={(value: string) => {
                  setValue('cabinClass', value as CABIN_CLASS_TYPE, {
                    shouldDirty: true,
                  })
                }}
              />
            )}

            {/* Package Deals */}
            {watchFlightType === ROUND_TRIP && (
              <div className={getInputClass('packageDeals', watchFlightType, useCompactLayout)}>
                <HorizonBundleAndSave
                  productType='flights'
                  checkedValues={bundleSelections}
                  onSelectionChange={handleBundleSelectionChange}
                  tripType={watchTripType}
                />
              </div>
            )}

            {/* Search Button */}
            <Button
              type='primary'
              size='lg'
              buttonType='submit'
              className={cn(
                '[grid-area:unset]',
                !useCompactLayout && 'lg:[grid-area:unset]',
                watchFlightType === MULTI_DESTINATION ? 'sm:[grid-area:unset]' : 'sm:[grid-area:submit]',
                watchFlightType === ONE_WAY && !useCompactLayout && 'sm:col-span-full',
                isMobile ? 'fixed right-4 bottom-4 left-4' : '',
              )}
            >
              {PLACEHOLDERS.SEARCH_BUTTON}
            </Button>
          </div>
        </div>
      </form>
    </div>
  )
}
